import { throwIfAborted } from "@ext/print/utils/pagination/abort";
import NodePaginator from "@ext/print/utils/pagination/NodePaginator";
import type Paginator from "@ext/print/utils/pagination/Paginator";
import PaginatorBase from "@ext/print/utils/pagination/Paginator";

/**
 * Splits one OpenAPI block across pages.
 *
 * Expanded for print the block is far taller than a page, and it is a single top-level article node, so
 * without a handler the paginator force-fits it whole: everything past the first page lands under the page
 * box's `overflow: hidden` and is silently lost. This paginates it the way a note is paginated — the chrome
 * around a list is cloned onto each new page and the list's items move into it one at a time — applied at
 * every level of the block that is a container around a list: the page shell, the header, the description,
 * the grid, a tag section whose operation cards do not fit together (see `stackOf` in openApiHandler).
 *
 * Clones carry `data-static`, which tells the viewer's custom elements they are page chrome and not a
 * document: an `<openapi-doc>` clone would otherwise reconnect and render itself as an empty one.
 */
const cloneChrome = <T extends HTMLElement>(node: T): T => {
	const clone = node.cloneNode(false) as T;
	// Ids are anchors into the document; a continuation is not the thing the anchor points at.
	clone.removeAttribute("id");
	if (clone.tagName.includes("-")) clone.setAttribute("data-static", "");
	return clone;
};

/**
 * A node whose chrome repeats on every page its content spills onto. `chain` is the wrappers this paginator
 * owns, outermost first; the last one holds the items that move.
 */
/**
 * Set on every copy of the chrome except the one the run ends in, for anything that should be drawn once at
 * the end and not on each page along the way — the rule under the document header, above all.
 */
export const CONTINUES_ATTRIBUTE = "data-print-continues";

export abstract class ChainPaginator extends NodePaginator<HTMLElement> {
	private _outerContainer: HTMLElement;
	private _chromes: HTMLElement[] = [];
	/** The chrome of the page the run opens on — where headings that must not repeat are put back. */
	protected _firstChrome: HTMLElement;

	/** Wrappers this paginator repeats, outermost first. The innermost one receives the items. */
	protected abstract _chain(): HTMLElement[];

	/** What the run opens with, by level of the chain — see `_reserveIntro`. */
	private _intro: Element[][] = [];

	/** How many items the page held when the gap below the last of them was booked — see `createPage`. */
	private _gapBookedAfter: number | undefined;

	async paginateNode() {
		const chain = this._chain();
		// Everything beside the list at each level — the document header, the operations heading, a section
		// head — opens the run and must not repeat on its continuations, so it is taken out of the way here
		// and put back into the first chrome once the items have been dealt out.
		const intro = chain
			.slice(0, -1)
			.map((wrapper, level) => [...wrapper.children].filter((child) => child !== chain[level + 1]));

		this._intro = intro;
		this._buildChrome();
		this._addChromeDimension();
		this._reserveIntro();
		await super.paginateSource(chain[chain.length - 1]);

		this.parentPaginator.currentContainer.appendChild(this._outerContainer);
		this.setMarginBottom();
		this.node.remove();
		// Every copy is made by `cloneChrome`, which drops the id — right for a continuation, wrong for the copy
		// the run opens with: that one is the node, and an anchor into the document has to keep pointing at it.
		if (this.node.id) this._firstChrome.id = this.node.id;

		let host = this._firstChrome;
		for (const children of intro) {
			// Prepending keeps the wrapper clone last, which is where the next level lives.
			children.reverse().forEach((child) => host.prepend(child));
			host = host.lastElementChild as HTMLElement;
		}

		// Which copy is the last one is only known once the run is over.
		for (const chrome of this._chromes) chrome.setAttribute(CONTINUES_ATTRIBUTE, "");
		this._outerContainer.removeAttribute(CONTINUES_ATTRIBUTE);
	}

	createPage() {
		this.parentPaginator.currentContainer.appendChild(this._outerContainer);
		this.cleanHeadingElementsIfNeed();
		throwIfAborted(PaginatorBase.controlInfo.signal);

		// Nothing of the run fitted the page it opened on: that copy of the chrome leaves the page empty and is
		// carried to the next one, and the intro, which is put back into it at the end, is drawn there too.
		const introMoves = !this.haveChildNodes() && this._outerContainer === this._firstChrome;

		if (this.haveChildNodes()) this._buildChrome();
		else this._outerContainer.remove();

		this.parentPaginator.createPage();
		this._addChromeDimension();
		if (introMoves) this._reserveIntro();
		this.setHeadings();
		// A heading carried onto the new page is followed there by whatever asked for the page — often a run of
		// its own, which is put on the page by that run and never through `tryFitElement`. The column holds them
		// a gap apart all the same; unbooked, «Responses» and the block under it came out 16px taller than booked.
		const carried = this.currentContainer.childElementCount;
		if (carried) {
			const chain = this._chain();
			this._reserveGap(chain[chain.length - 1]);
			this._gapBookedAfter = carried;
		}
		return this.currentContainer;
	}

	private _buildChrome() {
		const clones = this._chain().map(cloneChrome);
		clones.reduce((parent, child) => {
			parent.appendChild(child);
			return child;
		});
		this._outerContainer = clones[0];
		this.currentContainer = clones[clones.length - 1];
		this._firstChrome = this._firstChrome ?? this._outerContainer;
		this._chromes.push(this._outerContainer);
	}

	/**
	 * Books the content the run opens with — the document header, the operations heading, a section head, the
	 * head of an operation card. It is put back into the first copy of the chrome only after the items have
	 * been dealt out, so without this the page it lands on is filled against a height it is not going to have.
	 *
	 * Booked on whichever page that copy of the chrome ends up on, which is not always the page the run opens
	 * on: a run that places nothing at all leaves an empty chrome behind, and `createPage` carries it forward.
	 * A card whose head was booked on the page before the one it was drawn on printed 48px past the sheet.
	 */
	private _reserveIntro() {
		const nodeDimension = PaginatorBase.paginationInfo.nodeDimension;
		const chain = this._chain();
		this._intro.forEach((children, level) => {
			for (const child of children) {
				const dimension = nodeDimension.get(child as HTMLElement);
				if (dimension) this.updateAccumulatedHeightDim(dimension);
				// Each of them is put back above the items, and its wrapper holds them a gap apart.
				this._reserveGap(chain[level]);
			}
		});
	}

	/**
	 * The repeated chrome takes vertical space of its own on the new page, and the first item on that page has
	 * to be measured against what is left of it.
	 *
	 * Everything the wrapper adds around its content counts, not only padding: `.doc-header` carries a 20px
	 * bottom margin and a rule under it, and a page that budgeted for neither fitted one element too many and
	 * printed it over its own footer.
	 */
	private _addChromeDimension() {
		const nodeDimension = PaginatorBase.paginationInfo.nodeDimension;
		const height = this._chain().reduce((sum, wrapper) => {
			const dimension = nodeDimension.get(wrapper);
			if (!dimension) return sum;
			return sum + dimension.paddingH + (dimension.borderH ?? 0) + dimension.marginTop + dimension.marginBottom;
		}, 0);
		if (!height) return;

		this.updateAccumulatedHeightDim({ height, marginTop: 0, marginBottom: 0, paddingH: height });
	}

	/**
	 * Books the space the container itself puts between two of its children.
	 *
	 * `NodeDimensions` measures a node's height, its margins, its padding and its border — a flex `gap` is
	 * none of those, and every container the viewer flows is a flex column that has one: the page shell and
	 * `.doc-body` hold their children 16px apart, a section its own 8. Unbooked, those gaps are drawn on top
	 * of a budget that assumed none, and a page filled to within a gap of its height prints its last card
	 * over its own footer — 18px past the box in the export this was found on.
	 *
	 * Read off the original wrapper and never off the chrome clone: the clones are detached while the items
	 * are dealt out, and a detached element has no computed style to read.
	 */
	private _reserveGap(original: HTMLElement) {
		// `normal` both for a container that declares no gap and for anything that is not a flex or grid box.
		const gap = Number.parseFloat(getComputedStyle(original).rowGap) || 0;
		if (!gap) return;

		this.updateAccumulatedHeightDim({ height: gap, marginTop: 0, marginBottom: 0, paddingH: gap });
	}

	/** Every item but the first on a page is preceded by one gap of the container it is dealt into. */
	public tryFitElement(node: HTMLElement, force = false): boolean {
		const chain = this._chain();
		const placed = this.currentContainer.childElementCount;
		if (placed && placed !== this._gapBookedAfter) this._reserveGap(chain[chain.length - 1]);
		this._gapBookedAfter = undefined;

		return super.tryFitElement(node, force);
	}
}

/** One section — a tag group or Schemas: the head opens the run, the cards flow onto as many pages as needed. */
class OpenApiSectionPaginator extends ChainPaginator {
	private _wrappers: HTMLElement[];

	constructor(section: HTMLElement, stack: HTMLElement, parentPaginator: Paginator) {
		super(section, parentPaginator);
		this._wrappers = wrappersBetween(section, stack);
	}

	protected _chain(): HTMLElement[] {
		return this._wrappers;
	}
}

/** The block as a whole: the `<openapi-doc>` page shell repeats, its own children flow. */
export class OpenApiPaginator extends ChainPaginator {
	private _wrappers: HTMLElement[];

	constructor(block: HTMLElement, parentPaginator: Paginator) {
		super(block, parentPaginator);
		this._wrappers = wrappersBetween(block, contentHostOf(block));
	}

	static contentHost(node: HTMLElement): HTMLElement | null {
		return contentHostOf(node);
	}

	protected _chain(): HTMLElement[] {
		return this._wrappers;
	}
}

/**
 * The viewer's page shell. Its children are what flows: the header, the operations heading, the grid.
 *
 * It used to be the `.grid` inside it, which made everything above the grid a single indivisible run — and a
 * description longer than a page cannot be one. Each of those children is a container of its own and gets
 * split the same way when it has to (see `stackOf` in openApiHandler), so the shell is where the block stops
 * being one node and starts being a list.
 */
const contentHostOf = (block: HTMLElement): HTMLElement | null =>
	block.querySelector<HTMLElement>("openapi-doc > main.page");

/** The wrapper chain from `outer` down to `inner`, both included. */
const wrappersBetween = (outer: HTMLElement, inner: HTMLElement): HTMLElement[] => {
	// A container whose own children flow: one wrapper, no chain to walk. Walking would run past `outer` and
	// collect every ancestor up to the document.
	if (outer === inner) return [inner];

	const chain: HTMLElement[] = [inner];
	let node = inner.parentElement;
	while (node && node !== outer) {
		chain.unshift(node as HTMLElement);
		node = node.parentElement;
	}
	chain.unshift(outer);
	return chain;
};

export { OpenApiSectionPaginator };
export default OpenApiPaginator;
